import { useState, useRef, useId } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, User, MapPin, Mail, ArrowRight, CheckCircle2, AlertCircle, Tractor, Building2, Trees, HelpCircle } from "lucide-react";

// Lead trafia do mail.php na tym samym serwerze (dhosting) — e-mail na biuro@ + archiwum.
const LEAD_ENDPOINT = "/mail.php";

const PHONE_REGEX = /^(\+?48)?[\s-]?\d{3}[\s-]?\d{3}[\s-]?\d{3}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PROPERTY_TYPES = [
  { value: "grunt_rolny", label: "Działka rolna", icon: Tractor },
  { value: "dach", label: "Dach hali / budynku", icon: Building2 },
  { value: "dzialka_inna", label: "Inna działka", icon: Trees },
  { value: "inne", label: "Nie wiem / inne", icon: HelpCircle },
];

export default function LeadForm() {
  const formId = useId();
  const nameId = `${formId}-name`;
  const phoneId = `${formId}-phone`;
  const emailId = `${formId}-email`;
  const addressId = `${formId}-address`;
  const rodoId = `${formId}-rodo`;
  const propertyTypeGroupId = `${formId}-property-type`;

  const nameRef = useRef(null);
  const phoneRef = useRef(null);
  const emailRef = useRef(null);
  const addressRef = useRef(null);
  const rodoRef = useRef(null);
  const propertyTypeRef = useRef(null);

  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", property_type: "", website: "" });
  const [errors, setErrors] = useState({});
  const [rodo, setRodo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const validateField = (name, value) => {
    if (name === "name") {
      const v = value.trim();
      if (v && v.length < 3) return "Imię i nazwisko jest za krótkie.";
    }
    if (name === "phone") {
      if (!value.trim()) return "Podaj numer telefonu.";
      if (!PHONE_REGEX.test(value.trim())) return "Niepoprawny format numeru telefonu.";
    }
    if (name === "email") {
      const v = value.trim();
      if (v && !EMAIL_REGEX.test(v)) return "Niepoprawny format adresu e-mail.";
    }
    if (name === "address") {
      if (!value.trim()) return "Podaj lokalizację działki.";
      if (value.trim().length < 5) return "Adres jest za krótki — podaj miejscowość i gminę.";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const selectPropertyType = (value) => {
    setForm((f) => ({ ...f, property_type: value }));
    if (errors.property_type) {
      setErrors((prev) => ({ ...prev, property_type: "" }));
    }
  };

  const focusFirstError = (errs) => {
    if (errs.phone) phoneRef.current?.focus();
    else if (errs.address) addressRef.current?.focus();
    else if (errs.name) nameRef.current?.focus();
    else if (errs.email) emailRef.current?.focus();
    else if (errs.rodo) rodoRef.current?.focus();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {
      name: validateField("name", form.name),
      phone: validateField("phone", form.phone),
      email: validateField("email", form.email),
      address: validateField("address", form.address),
      rodo: rodo ? "" : "Wymagana jest zgoda na przetwarzanie danych.",
    };
    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) {
      focusFirstError(newErrors);
      return;
    }

    setLoading(true);
    try {
      await axios.post(LEAD_ENDPOINT, { ...form, rodo_consent: true });
      setSubmitted(true);
      toast.success("Dziękujemy! Odezwiemy się w ciągu 24 godzin.");
    } catch {
      toast.error("Wystąpił błąd. Spróbuj ponownie lub zadzwoń: +48 512 929 641.");
    } finally {
      setLoading(false);
    }
  };

  const toggleRodo = () => {
    const next = !rodo;
    setRodo(next);
    if (next && errors.rodo) setErrors((prev) => ({ ...prev, rodo: "" }));
  };

  const handleRodoKey = (e) => {
    if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      toggleRodo();
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-8" data-testid="form-success" role="status" aria-live="polite">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="font-bold text-slate-900 text-xl mb-2">
          Zgłoszenie wysłane!
        </h3>
        <p className="text-slate-600 text-sm leading-relaxed">
          Nasz specjalista skontaktuje się z Tobą w ciągu 24 godzin roboczych,
          by omówić potencjał Twojej nieruchomości.
        </p>
      </div>
    );
  }

  const inputCls = (hasError) =>
    `w-full pl-10 pr-4 h-12 rounded-xl border text-sm focus:outline-none focus:ring-2 bg-white transition-shadow ${
      hasError
        ? "border-red-400 focus:ring-red-500 focus:border-transparent"
        : "border-slate-200 focus:ring-emerald-500 focus:border-transparent"
    }`;

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="lead-form" noValidate>
      {/* honeypot anty-spam — ukryte pole; ludzie go nie wypełnią, boty tak */}
      <input
        type="text"
        name="website"
        value={form.website}
        onChange={handleChange}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
      />
      <fieldset>
        <legend
          id={propertyTypeGroupId}
          className="text-sm font-medium text-slate-700 mb-1.5 block"
        >
          Co chcesz wydzierżawić? <span className="text-slate-400 font-normal">(opcjonalnie)</span>
        </legend>
        <div
          ref={propertyTypeRef}
          role="radiogroup"
          aria-labelledby={propertyTypeGroupId}
          aria-invalid={!!errors.property_type}
          aria-describedby={errors.property_type ? `${propertyTypeGroupId}-err` : undefined}
          tabIndex={-1}
          className="grid grid-cols-2 gap-2 focus:outline-none"
        >
          {PROPERTY_TYPES.map((pt) => {
            const Icon = pt.icon;
            const selected = form.property_type === pt.value;
            return (
              <button
                key={pt.value}
                type="button"
                role="radio"
                aria-checked={selected}
                onClick={() => selectPropertyType(pt.value)}
                className={`group flex items-center gap-2 px-3 py-2.5 rounded-xl border text-left text-xs sm:text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-1 ${
                  selected
                    ? "bg-emerald-700 border-emerald-700 text-white shadow-md shadow-emerald-900/20"
                    : errors.property_type
                    ? "bg-white border-red-300 text-slate-700 hover:border-red-400"
                    : "bg-white border-slate-200 text-slate-700 hover:border-emerald-400 hover:bg-emerald-50/50"
                }`}
                data-testid={`property-type-${pt.value}`}
              >
                <Icon
                  className={`w-4 h-4 flex-shrink-0 ${
                    selected ? "text-white" : "text-emerald-700 group-hover:text-emerald-800"
                  }`}
                  aria-hidden="true"
                />
                <span className="leading-tight">{pt.label}</span>
              </button>
            );
          })}
        </div>
        {errors.property_type && (
          <p
            id={`${propertyTypeGroupId}-err`}
            role="alert"
            className="mt-1.5 text-xs text-red-600 flex items-center gap-1"
          >
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
            {errors.property_type}
          </p>
        )}
      </fieldset>

      <div>
        <label htmlFor={nameId} className="text-sm font-medium text-slate-700 mb-1.5 block">
          Imię i nazwisko <span className="text-slate-400 font-normal">(opcjonalnie)</span>
        </label>
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            ref={nameRef}
            id={nameId}
            type="text"
            name="name"
            autoComplete="name"
            value={form.name}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? `${nameId}-err` : undefined}
            placeholder="np. Jan Kowalski"
            className={inputCls(errors.name)}
            data-testid="lead-name-input"
          />
        </div>
        {errors.name && (
          <p id={`${nameId}-err`} role="alert" className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            {errors.name}
          </p>
        )}
      </div>

      <div>
        <label htmlFor={phoneId} className="text-sm font-medium text-slate-700 mb-1.5 block">
          Numer telefonu <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            ref={phoneRef}
            id={phoneId}
            type="tel"
            name="phone"
            autoComplete="tel"
            inputMode="tel"
            value={form.phone}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? `${phoneId}-err` : `${phoneId}-help`}
            placeholder="np. 600 100 200"
            className={inputCls(errors.phone)}
            data-testid="lead-phone-input"
          />
        </div>
        {errors.phone ? (
          <p id={`${phoneId}-err`} role="alert" className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            {errors.phone}
          </p>
        ) : (
          <p id={`${phoneId}-help`} className="mt-1.5 text-xs text-slate-500">
            Oddzwonimy w ciągu 24 godzin.
          </p>
        )}
      </div>

      <div>
        <label htmlFor={emailId} className="text-sm font-medium text-slate-700 mb-1.5 block">
          Adres e-mail <span className="text-slate-400 font-normal">(opcjonalnie)</span>
        </label>
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            ref={emailRef}
            id={emailId}
            type="email"
            name="email"
            autoComplete="email"
            inputMode="email"
            value={form.email}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? `${emailId}-err` : `${emailId}-help`}
            placeholder="np. jan.kowalski@example.pl"
            className={inputCls(errors.email)}
            data-testid="lead-email-input"
          />
        </div>
        {errors.email ? (
          <p id={`${emailId}-err`} role="alert" className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
            {errors.email}
          </p>
        ) : (
          <p id={`${emailId}-help`} className="mt-1.5 text-xs text-slate-500">
            Na e-mail wyślemy podsumowanie i dokumenty po rozmowie.
          </p>
        )}
      </div>

      <div>
        <label htmlFor={addressId} className="text-sm font-medium text-slate-700 mb-1.5 block">
          Adres / lokalizacja działki <span className="text-red-500" aria-hidden="true">*</span>
        </label>
        <div className="relative">
          <MapPin className="absolute left-3 top-3.5 w-4 h-4 text-slate-400 pointer-events-none" />
          <textarea
            ref={addressRef}
            id={addressId}
            name="address"
            autoComplete="street-address"
            value={form.address}
            onChange={handleChange}
            onBlur={handleBlur}
            aria-invalid={!!errors.address}
            aria-describedby={errors.address ? `${addressId}-err` : undefined}
            placeholder="np. ul. Leśna 5, gmina Wiązowna, powiat otwocki"
            rows={2}
            className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 bg-white resize-none transition-shadow ${
              errors.address
                ? "border-red-400 focus:ring-red-500 focus:border-transparent"
                : "border-slate-200 focus:ring-emerald-500 focus:border-transparent"
            }`}
            data-testid="lead-address-input"
          />
        </div>
        {errors.address && (
          <p id={`${addressId}-err`} role="alert" className="mt-1.5 text-xs text-red-600 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            {errors.address}
          </p>
        )}
      </div>

      <div className="pt-1">
        <div className="flex items-start gap-3">
          <div
            ref={rodoRef}
            id={rodoId}
            role="checkbox"
            aria-checked={rodo}
            aria-invalid={!!errors.rodo}
            aria-describedby={errors.rodo ? `${rodoId}-err` : `${rodoId}-label`}
            tabIndex={0}
            onClick={toggleRodo}
            onKeyDown={handleRodoKey}
            className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${
              rodo
                ? "bg-emerald-600 border-emerald-600"
                : errors.rodo
                ? "border-red-400 bg-white hover:border-red-500"
                : "border-slate-300 bg-white hover:border-emerald-400"
            }`}
            data-testid="rodo-checkbox"
          >
            {rodo && (
              <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path
                  d="M2 6l3 3 5-5"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <label
            id={`${rodoId}-label`}
            htmlFor={rodoId}
            className="text-xs text-slate-600 leading-relaxed cursor-pointer select-none"
            onClick={(e) => {
              e.preventDefault();
              toggleRodo();
            }}
          >
            Wyrażam zgodę na przetwarzanie moich danych osobowych przez Telecom BTS
            w celu kontaktu i obsługi mojego zapytania.{" "}
            <a
              href="/polityka-prywatnosci"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-emerald-700 underline hover:text-emerald-800"
            >
              Polityka prywatności
            </a>{" "}
            <span className="text-slate-400"> (wymagane)</span>
          </label>
        </div>
        {errors.rodo && (
          <p id={`${rodoId}-err`} role="alert" className="mt-1.5 text-xs text-red-600 flex items-center gap-1 ml-8">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            {errors.rodo}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 disabled:cursor-not-allowed text-white font-semibold py-3.5 rounded-full transition-all duration-200 hover:-translate-y-0.5 disabled:translate-y-0 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 flex items-center justify-center gap-2 text-sm shadow-lg shadow-emerald-900/20"
        data-testid="lead-submit-button"
      >
        {loading ? (
          <>
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" aria-hidden="true" />
            <span>Wysyłanie...</span>
          </>
        ) : (
          <>
            <span>Sprawdź potencjał swojej działki</span>
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </>
        )}
      </button>

      <p className="text-xs text-slate-500 text-center">
        Twoje dane są bezpieczne. Nie wysyłamy spamu.
      </p>
    </form>
  );
}
